import { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import useAccessibility from 'volto-ministerio-publico-federal/hooks/useAccessibility';

import ContrastBootScript from './ContrastBootScript';
import { SuperiorBar } from './SuperiorBar';
import { Menu } from './Menu';
import MegaMenu from './MegaMenu';
import MenuMobile from './MenuMobile';
import { menuItemKey, type MenuItem } from './mainMenu';
import { ACCESSIBILITY_URL, SITEMAP_URL } from './constants';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useOnClickOutside } from './hooks/useOnClickOutside';
import { useFocusOutside } from './hooks/useFocusOutside';

interface HeaderProps {
  pathname: string;
  props?: { items?: MenuItem[] };
}

const Header: React.FC<HeaderProps> = ({ pathname, ...props }) => {
  const menuItems: MenuItem[] = props?.props?.items ?? [];

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const site = useSelector((state: any) => state.site.data);

  const title = site?.['plone.site_title'];
  const subtitle = site?.['plone.site_subtitle'] || 'Portal Institucional';
  const homeUrl = site?.['plone.site_url'] || '/';

  const a11y = useAccessibility();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);
  const navRef = useRef(null);
  const a11yRef = useRef(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const openItem: MenuItem | null =
    (openDropdown &&
      menuItems.find((item) => menuItemKey(item) === openDropdown)) ||
    null;

  // Fechar pelo teclado ou pelo botão devolve o foco ao gatilho; o clique
  // fora não mexe no foco, que já foi para onde o usuário clicou.
  const closeMegaMenu = useCallback(() => {
    if (openDropdown) triggerRefs.current[openDropdown]?.focus();
    setOpenDropdown(null);
  }, [openDropdown]);

  /*
  ------------------------------------------------------------
  Convivência entre os painéis do mobile
  ------------------------------------------------------------
  O menu de opções desce por cima da barra azul e do painel de navegação, e
  por isso é exclusivo em relação aos dois. Já a navegação e a busca dividem a
  barra sem disputar espaço (o hambúrguer à esquerda, o campo à direita) e
  podem ficar abertas ao mesmo tempo — quem abriu o menu e resolveu buscar não
  perde o lugar.
  ------------------------------------------------------------
  */
  const toggleMobile = useCallback(() => {
    setOverflowOpen(false);
    setMobileOpen((open) => !open);
  }, []);

  const toggleSearch = useCallback(() => {
    setOverflowOpen(false);
    setSearchOpen((open) => !open);
  }, []);

  const toggleOverflow = useCallback(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setOverflowOpen((open) => !open);
  }, []);

  // Escape fecha tudo, mas devolve o foco a um único alvo — o mais externo
  // que estava aberto. O menu mobile fica de fora: o hambúrguer continua
  // visível e o foco não está preso.
  const closeEverything = useCallback(() => {
    if (overflowOpen) overflowButtonRef.current?.focus({ preventScroll: true });
    else if (searchOpen)
      searchToggleRef.current?.focus({ preventScroll: true });
    else closeMegaMenu();

    setOpenDropdown(null);
    setMobileOpen(false);
    setSearchOpen(false);
    setOverflowOpen(false);
    setA11yOpen(false);
  }, [closeMegaMenu, overflowOpen, searchOpen]);

  // O painel é irmão do <nav>, então o clique dentro dele também precisa
  // contar como "dentro" — senão o mega menu fecharia ao primeiro clique.
  useOnClickOutside(
    openDropdown != null,
    [navRef, megaMenuRef],
    useCallback(() => setOpenDropdown(null), []),
  );

  // Pelo teclado, o Tab depois do último item do painel levava o foco para o
  // conteúdo atrás dele, com o painel ainda aberto por cima (bug 11145). O
  // foco não é preso (menu não é diálogo): o painel fecha quando ele sai.
  useFocusOutside(
    openDropdown != null,
    [navRef, megaMenuRef],
    useCallback(() => setOpenDropdown(null), []),
  );

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // O hambúrguer entra na lista: sem ele, o `mousedown` no botão fecharia o
  // painel aqui e o `onClick` o reabriria logo em seguida. A lupa também, já
  // que a busca pode abrir com o menu aberto. O clique no fundo cinza cai fora
  // de tudo e fecha o painel.
  useOnClickOutside(
    mobileOpen,
    [mobilePanelRef, burgerRef, searchToggleRef],
    closeMobile,
  );

  useOnClickOutside(
    overflowOpen,
    [overflowRef],
    useCallback(() => setOverflowOpen(false), []),
  );

  useOnClickOutside(
    a11yOpen,
    [a11yRef],
    useCallback(() => setA11yOpen(false), []),
  );

  useEscapeKey(closeEverything);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
    setSearchOpen(false);
    setOverflowOpen(false);
    setA11yOpen(false);
  }, [pathname]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div id="site-header" className="mpf-topbar">
      <ContrastBootScript />
      <SuperiorBar
        title={title}
        subtitle={subtitle}
        homeUrl={homeUrl}
        a11y={a11y}
        a11yOpen={a11yOpen}
        setA11yOpen={setA11yOpen}
        a11yRef={a11yRef}
        accessibilityLink={ACCESSIBILITY_URL}
        sitemap_link={SITEMAP_URL}
        overflowOpen={overflowOpen}
        onToggleOverflow={toggleOverflow}
        overflowRef={overflowRef}
        overflowButtonRef={overflowButtonRef}
      />
      <Menu
        navItems={menuItems}
        navRef={navRef}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
        triggerRefs={triggerRefs}
        mobileOpen={mobileOpen}
        onToggleMobile={toggleMobile}
        burgerRef={burgerRef}
        searchOpen={searchOpen}
        onToggleSearch={toggleSearch}
        searchToggleRef={searchToggleRef}
      />

      {/* Irmão da barra azul (e não filho do <li>) para ocupar a largura
          inteira da tela; o posicionamento se apoia no .mpf-topbar. */}
      {isMounted && openItem && (
        <MegaMenu
          item={openItem}
          onClose={closeMegaMenu}
          panelRef={megaMenuRef}
        />
      )}

      {/* Mesma ancoragem do mega menu: o painel nasce colado na barra azul e
          sobrepõe a página, coberta pelo fundo cinza. */}
      {mobileOpen && (
        <MenuMobile
          navItems={menuItems}
          panelRef={mobilePanelRef}
          onNavigate={closeMobile}
        />
      )}
    </div>
  );
};

export default Header;
